import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useHeaderActions } from "../../context/HeaderActionsContext";
import {
  schedulesService,
  type DashboardSchedule,
} from "../../services/schedules.service";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScheduleRow } from "./ScheduleRow";
import { ScheduleSummaryCards } from "./ScheduleSummaryCards";

function sortSchedules(schedules: DashboardSchedule[]): DashboardSchedule[] {
  return [...schedules].sort((a, b) => a.name.localeCompare(b.name));
}

function replaceSchedule(
  schedules: DashboardSchedule[],
  updatedSchedule: DashboardSchedule,
): DashboardSchedule[] {
  return schedules.map((schedule) =>
    schedule.id === updatedSchedule.id ? updatedSchedule : schedule,
  );
}

function setContains(
  scheduleIds: Set<string>,
  scheduleId: string,
  shouldContain: boolean,
): Set<string> {
  const next = new Set(scheduleIds);
  if (shouldContain) {
    next.add(scheduleId);
  } else {
    next.delete(scheduleId);
  }
  return next;
}

export function SchedulesAdmin() {
  const [schedules, setSchedules] = useState<DashboardSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyScheduleIds, setBusyScheduleIds] = useState<Set<string>>(
    () => new Set(),
  );
  const { setActions } = useHeaderActions();

  const loadSchedules = useCallback(async () => {
    try {
      setIsLoading(true);
      setSchedules(await schedulesService.listSchedules());
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to load schedules",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    setActions(
      <Button
        variant="outline"
        size="icon"
        onClick={loadSchedules}
        disabled={isLoading}
        className="h-8 w-8"
        title="Refresh schedules"
      >
        <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
      </Button>,
    );
    return () => setActions(null);
  }, [isLoading, loadSchedules, setActions]);

  const setScheduleBusy = useCallback((scheduleId: string, busy: boolean) => {
    setBusyScheduleIds((previous) => setContains(previous, scheduleId, busy));
  }, []);

  const updateSchedule = useCallback((schedule: DashboardSchedule) => {
    setSchedules((previous) => replaceSchedule(previous, schedule));
  }, []);

  const toggleSchedule = useCallback(
    async (schedule: DashboardSchedule, enabled: boolean) => {
      setScheduleBusy(schedule.id, true);
      try {
        updateSchedule(
          await schedulesService.setScheduleEnabled(schedule.id, enabled),
        );
        toast.success(`${schedule.name} ${enabled ? "enabled" : "disabled"}`);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to update schedule",
        );
      } finally {
        setScheduleBusy(schedule.id, false);
      }
    },
    [setScheduleBusy, updateSchedule],
  );

  const runScheduleNow = useCallback(
    async (schedule: DashboardSchedule) => {
      setScheduleBusy(schedule.id, true);
      try {
        const updatedSchedule = await schedulesService.runScheduleNow(
          schedule.id,
        );
        updateSchedule(updatedSchedule);
        if (updatedSchedule.lastError) {
          toast.error(updatedSchedule.lastError);
        } else {
          toast.success(`${schedule.name} completed`);
        }
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to run schedule",
        );
      } finally {
        setScheduleBusy(schedule.id, false);
      }
    },
    [setScheduleBusy, updateSchedule],
  );

  const sortedSchedules = useMemo(() => sortSchedules(schedules), [schedules]);

  if (isLoading && schedules.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Loading schedules...
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <ScheduleSummaryCards
        busyCount={busyScheduleIds.size}
        schedules={sortedSchedules}
      />

      <Card className="min-h-0 rounded-lg py-0">
        <CardHeader className="border-b px-5 py-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="size-4" />
            Scheduled Jobs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0 px-0">
          {sortedSchedules.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">
              No schedules registered.
            </div>
          ) : (
            sortedSchedules.map((schedule) => (
              <ScheduleRow
                key={schedule.id}
                isBusy={busyScheduleIds.has(schedule.id)}
                onRunNow={(selectedSchedule) =>
                  void runScheduleNow(selectedSchedule)
                }
                onToggle={(selectedSchedule, enabled) =>
                  void toggleSchedule(selectedSchedule, enabled)
                }
                schedule={schedule}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
